
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';
import type { SettingsTabProps } from './types';

const GeneralSettingsTab: React.FC<SettingsTabProps> = ({ settings, onSettingsChange }) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const updateSettingsMutation = useMutation({
    mutationFn: async (settingsToUpdate: Record<string, any>) => {
      const updatePromises = Object.entries(settingsToUpdate).map(async ([key, value]) => {
        const jsonValue: Json = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null
          ? value
          : JSON.stringify(value);
          
        return supabase
          .from('website_settings')
          .upsert({ 
            key, 
            value: jsonValue, 
            description: `Setting for ${key}` 
          }, {
            onConflict: 'key'
          });
      });
      
      const results = await Promise.all(updatePromises);
      
      // Check for errors
      const failedUpdates = results.filter(result => result.error);
      if (failedUpdates.length > 0) {
        throw new Error(`Failed to update ${failedUpdates.length} settings`);
      }
      
      return results;
    },
    onSuccess: () => {
      // Gentle query cache update
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['website-settings'] });
      }, 500);
      toast.success('General settings updated successfully');
      setErrors({});
      setIsSubmitting(false);
    },
    onError: (error: any) => {
      console.error('Settings update error:', error);
      toast.error('Failed to update general settings');
      setErrors({ general: error.message || 'Failed to save settings' });
      setIsSubmitting(false);
    },
  });

  const validateSettings = () => {
    const newErrors: Record<string, string> = {};
    
    if (!settings.site_name?.trim()) {
      newErrors.site_name = 'Site name is required';
    }
    
    if (settings.site_url && !settings.site_url.match(/^https?:\/\/.+/)) {
      newErrors.site_url = 'Site URL must be a valid URL starting with http:// or https://';
    }
    
    if (settings.admin_email && !settings.admin_email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      newErrors.admin_email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (isSubmitting) return; // Prevent double submission
    
    if (!validateSettings()) {
      toast.error('Please fix the validation errors before saving');
      return;
    }

    setIsSubmitting(true);
    
    const generalSettings = {
      site_name: settings.site_name,
      site_description: settings.site_description,
      site_url: settings.site_url,
      admin_email: settings.admin_email,
      maintenance_mode: settings.maintenance_mode,
      allow_registration: settings.allow_registration,
    };

    updateSettingsMutation.mutate(generalSettings);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>General Settings</CardTitle>
        <CardDescription>Configure basic website information and settings</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {errors.general && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {errors.general}
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="site_name">Site Name *</Label>
            <Input
              id="site_name"
              value={settings.site_name || ''}
              onChange={(e) => onSettingsChange('site_name', e.target.value)}
              className={errors.site_name ? 'border-red-500' : ''}
            />
            {errors.site_name && (
              <p className="text-sm text-red-600">{errors.site_name}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="site_url">Site URL</Label>
            <Input
              id="site_url"
              value={settings.site_url || ''}
              onChange={(e) => onSettingsChange('site_url', e.target.value)}
              placeholder="https://example.com"
              className={errors.site_url ? 'border-red-500' : ''}
            />
            {errors.site_url && (
              <p className="text-sm text-red-600">{errors.site_url}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="site_description">Site Description</Label>
          <Textarea
            id="site_description"
            value={settings.site_description || ''}
            onChange={(e) => onSettingsChange('site_description', e.target.value)}
            placeholder="A brief description of your website"
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="admin_email">Admin Email</Label>
          <Input
            id="admin_email"
            type="email"
            value={settings.admin_email || ''}
            onChange={(e) => onSettingsChange('admin_email', e.target.value)}
            placeholder="admin@example.com"
            className={errors.admin_email ? 'border-red-500' : ''}
          />
          {errors.admin_email && (
            <p className="text-sm text-red-600">{errors.admin_email}</p>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="space-y-1">
            <Label htmlFor="maintenance_mode">Maintenance Mode</Label>
            <p className="text-sm text-gray-600">Enable to show maintenance page to visitors</p>
          </div>
          <Switch
            id="maintenance_mode"
            checked={settings.maintenance_mode || false}
            onCheckedChange={(checked) => onSettingsChange('maintenance_mode', checked)}
          />
        </div>

        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="space-y-1">
            <Label htmlFor="allow_registration">Allow Registration</Label>
            <p className="text-sm text-gray-600">Allow new users to register accounts</p>
          </div>
          <Switch
            id="allow_registration"
            checked={settings.allow_registration || false}
            onCheckedChange={(checked) => onSettingsChange('allow_registration', checked)}
          />
        </div>

        <div className="flex justify-end pt-4">
          <Button 
            onClick={handleSave} 
            disabled={isSubmitting}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSubmitting ? 'Saving...' : 'Save General Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default GeneralSettingsTab;
